import React from "react";
import { useNavigate } from "react-router-dom";
import {
  useUsers,
  useSetUserApproval,
  useSetUserAdmin,
  useSetUserPermissions,
} from "@/hooks/useUsers";
import { useMe } from "@/hooks/useMe";
import { PageHeader } from "@/components/PageHeader";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Shield } from "lucide-react";
import { toast } from "sonner";
import type { UserPermissions } from "@/types";

// Agrupado por secao: cada secao tem um par View/Edit
const SECTIONS: {
  label: string;
  view: keyof UserPermissions;
  edit: keyof UserPermissions;
}[] = [
  { label: "Clients", view: "can_view_clients", edit: "can_edit_clients" },
  { label: "Funds", view: "can_view_funds", edit: "can_edit_funds" },
  { label: "Assets", view: "can_view_assets", edit: "can_edit_assets" },
];

export default function UserManagement() {
  const { data: me, isLoading: meLoading } = useMe();
  const navigate = useNavigate();
  const { data: users, isLoading } = useUsers();
  const setApproval = useSetUserApproval();
  const setAdmin = useSetUserAdmin();
  const setPerms = useSetUserPermissions();

  if (!meLoading && me && !me.user.isAdmin) {
    navigate("/");
    return null;
  }

  const toggleApproval = async (id: string, current: boolean) => {
    try {
      await setApproval.mutateAsync({ id, approved: !current });
      toast.success(current ? "Access revoked" : "Access approved");
    } catch {
      toast.error("Error updating");
    }
  };

  const togglePerm = async (
    id: string,
    key: keyof UserPermissions,
    current: boolean
  ) => {
    try {
      const newVal = !current;
      const extra: Partial<UserPermissions> = {};
      // Sync v1: can_view_clients implica can_view_investments
      if (key === "can_view_clients") extra.can_view_investments = newVal;
      // can_edit implica can_view
      if (key === "can_edit_clients" && newVal) {
        extra.can_view_clients = true;
        extra.can_view_investments = true;
      }
      if (key === "can_edit_funds" && newVal) extra.can_view_funds = true;
      if (key === "can_edit_assets" && newVal) extra.can_view_assets = true;
      // desmarcar View derruba o Edit correspondente
      if (key === "can_view_clients" && !newVal) extra.can_edit_clients = false;
      if (key === "can_view_funds" && !newVal) extra.can_edit_funds = false;
      if (key === "can_view_assets" && !newVal) extra.can_edit_assets = false;
      await setPerms.mutateAsync({ id, [key]: newVal, ...extra });
    } catch {
      toast.error("Error updating permission");
    }
  };

  const toggleAdmin = async (id: string, current: boolean) => {
    try {
      await setAdmin.mutateAsync({ id, is_admin: !current });
      toast.success(current ? "Admin removed" : "Promoted to admin");
    } catch {
      toast.error("Error changing admin status");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <PageHeader title="IR Jus Capital" subtitle="User Management" />
      <div className="container mx-auto px-4 py-8">
        <div className="rounded-lg border border-border bg-card overflow-x-auto">
          {isLoading ? (
            <div className="p-6 text-muted-foreground">Loading...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>E-mail</TableHead>
                  <TableHead>Registered</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-center">Admin</TableHead>
                  {SECTIONS.map((s) => (
                    <TableHead key={s.label} className="text-center border-l" colSpan={2}>
                      {s.label}
                    </TableHead>
                  ))}
                  <TableHead className="text-right border-l">Action</TableHead>
                </TableRow>
                <TableRow className="text-xs text-muted-foreground">
                  <TableHead colSpan={4} />
                  {SECTIONS.map((s) => (
                    <React.Fragment key={s.label}>
                      <TableHead className="text-center font-normal border-l">View</TableHead>
                      <TableHead className="text-center font-normal">Edit</TableHead>
                    </React.Fragment>
                  ))}
                  <TableHead className="border-l" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {(users ?? []).map((u) => (
                  <TableRow key={u.id}>
                    <TableCell className="font-medium">{u.email}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(u.created_at).toLocaleDateString("pt-BR")}
                    </TableCell>
                    <TableCell>
                      {u.approved ? (
                        <Badge className="bg-green-600 hover:bg-green-700">Approved</Badge>
                      ) : (
                        <Badge variant="secondary">Pending</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Checkbox
                        checked={u.is_admin}
                        disabled={u.id === me?.user.id}
                        onCheckedChange={() => toggleAdmin(u.id, u.is_admin)}
                      />
                    </TableCell>
                    {SECTIONS.map((s) => (
                      <React.Fragment key={s.label}>
                        <TableCell className="text-center border-l">
                          <Checkbox
                            checked={u.is_admin || u[s.view]}
                            disabled={u.is_admin || !u.approved}
                            onCheckedChange={() => togglePerm(u.id, s.view, u[s.view])}
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <Checkbox
                            checked={u.is_admin || u[s.edit]}
                            disabled={u.is_admin || !u.approved}
                            onCheckedChange={() => togglePerm(u.id, s.edit, u[s.edit])}
                          />
                        </TableCell>
                      </React.Fragment>
                    ))}
                    <TableCell className="text-right border-l">
                      <Button
                        variant={u.approved ? "outline" : "default"}
                        size="sm"
                        onClick={() => toggleApproval(u.id, u.approved)}
                        className="gap-1"
                        disabled={u.id === me?.user.id}
                      >
                        {u.approved ? (
                          <><XCircle className="h-4 w-4" /> Revoke</>
                        ) : (
                          <><CheckCircle className="h-4 w-4" /> Approve</>
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
        <p className="mt-4 text-xs text-muted-foreground flex items-center gap-2">
          <Shield className="h-3 w-3" />
          You cannot revoke yourself or demote yourself from admin (lockout protection).
        </p>
      </div>
    </div>
  );
}
